import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  LayoutDashboard, User, FileText,
  Building2, Briefcase, Settings, Database, LifeBuoy,
  Box, Home, Star, Share, Grid, Bot, TreePine, ArchiveRestore, BookOpen, Users, Shield,
  UserCircle, Activity, Trophy, Flame, Mail, Cog, ChevronDown, ChevronRight, Search,
  PanelLeftClose, PanelLeftOpen,
  Award, FileCheck, ListChecks, ScrollText, BarChart3, Target, ClipboardCheck, History
} from 'lucide-react';
import { usePermissions } from '../../hooks/usePermissions';
import { useLogo } from '../../contexts/LogoContext';
import customFrameworkService from '../../services/customFrameworkService';
import { toDisplayText } from '../../utils/displayText';
import './ModernSidebar.css';

const ModernSidebar = ({ isCollapsed, onToggle, user, isMobile = false, mobileOpen = false }) => {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const isRTL = i18n.language === 'ar';
  const lang = i18n.language?.startsWith('ar') ? 'ar' : 'en';
  const { hasPermission, isSystemAdmin } = usePermissions();
  const { logoPreview } = useLogo();
  const [customFrameworks, setCustomFrameworks] = useState([]);
  const [tooltip, setTooltip] = useState({ visible: false, text: '', top: 0, left: 0 });
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [paletteQuery, setPaletteQuery] = useState('');
  const [paletteHighlight, setPaletteHighlight] = useState(0);
  const paletteInputRef = useRef(null);

  const showTooltip = useCallback((e, text) => {
    if (!isCollapsed) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const rtl = document.documentElement.dir === 'rtl' || i18n.language === 'ar';
    setTooltip({
      visible: true,
      text,
      top: rect.top + rect.height / 2,
      left: rtl ? undefined : rect.right + 8,
      right: rtl ? (window.innerWidth - rect.left + 8) : undefined,
      isRTL: rtl,
    });
  }, [isCollapsed, i18n.language]);

  const hideTooltip = useCallback(() => {
    setTooltip(prev => ({ ...prev, visible: false }));
  }, []);

  // Close sidebar on route change on mobile
  useEffect(() => {
    if (isMobile && mobileOpen) {
      onToggle();
    }
  }, [location.pathname, isMobile, mobileOpen, onToggle]);

  // State for collapsible sections - all expanded by default
  const [expandedSections, setExpandedSections] = useState({
    core: true,
    frameworks: true,
    frameworkManagement: true,
    assessments: true,
    accreditation: true,
    administration: true,
    dataManagement: true,
    system: true,
    demo: true
  });

  // Load custom frameworks on mount
  useEffect(() => {
    loadCustomFrameworks();
  }, []);

  // Auto-expand section if it contains the active route
  useEffect(() => {
    const path = location.pathname;
    const isFrameworkPath = customFrameworks.some(fw => path.startsWith(`/frameworks/${fw.code}`));

    const sections = {
      core: ['/dashboard', '/ai-chat'],
      frameworks: isFrameworkPath,
      frameworkManagement: ['/framework/builder', '/frameworks/manage'],
      assessments: ['/create-assessment'],
      accreditation: ['/admin/accreditation'],
      administration: ['/admin/users', '/admin/permissions', '/admin/organizations'],
      dataManagement: ['/admin/backup', '/admin/restore'],
      system: ['/admin/ai-agent-config', '/settings', '/manual', '/help'],
      demo: ['/admin/self-assessment', '/admin/demo']
    };

    let activeSection = null;
    Object.keys(sections).forEach(sectionKey => {
      const sectionValue = sections[sectionKey];
      if (Array.isArray(sectionValue)) {
        if (sectionValue.some(p => path.startsWith(p))) {
          activeSection = sectionKey;
        }
      } else if (sectionValue === true) {
        activeSection = sectionKey;
      }
    });

    if (activeSection) {
      setExpandedSections(prev => ({
        ...prev,
        [activeSection]: true
      }));
    }
  }, [location.pathname, customFrameworks]);

  // Global Cmd+K / Ctrl+K shortcut
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setPaletteOpen(prev => !prev);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  // Focus palette input when opened
  useEffect(() => {
    if (paletteOpen) {
      setPaletteQuery('');
      setPaletteHighlight(0);
      setTimeout(() => paletteInputRef.current?.focus(), 30);
    }
  }, [paletteOpen]);

  const loadCustomFrameworks = async () => {
    try {
      const response = await customFrameworkService.listFrameworks('active');
      const frameworks = response.data || response;
      const visibleFrameworks = frameworks
        .filter(f => f.visible !== false && f.sidebar_position !== null)
        .sort((a, b) => (a.sidebar_position || 0) - (b.sidebar_position || 0));
      setCustomFrameworks(visibleFrameworks);
    } catch (error) {
      console.error('Failed to load custom frameworks:', error);
    }
  };

  const toggleSection = (sectionKey) => {
    setExpandedSections(prev => ({
      ...prev,
      [sectionKey]: !prev[sectionKey]
    }));
  };

  // Navigation items
  const coreItems = [
    { title: toDisplayText(t('landing.dashboard', 'Dashboard'), lang), path: '/dashboard', icon: LayoutDashboard, collapsedIcon: Home },
  ];

  const customFrameworkItems = customFrameworks.map(fw => {
    const lang = i18n.language || 'en';
    const iconStr = toDisplayText(fw.icon, lang) || '';
    const IconComponent = () => <span style={{ fontSize: '14px', lineHeight: 1 }}>{String(iconStr)}</span>;
    const CollapsedIconComponent = () => <span style={{ fontSize: '18px', lineHeight: 1 }}>{String(iconStr)}</span>;
    const nameAr = toDisplayText(fw.name_ar, lang);
    const nameEn = toDisplayText(fw.name_en, lang);
    const codeStr = toDisplayText(fw.code, 'en') || (typeof fw.code === 'string' ? fw.code : '') || '';
    const localizedName = (isRTL && nameAr ? nameAr : (nameEn || codeStr || '')) || '';
    return {
      title: String(localizedName),
      path: `/frameworks/${codeStr || 'unknown'}`,
      icon: IconComponent,
      collapsedIcon: CollapsedIconComponent,
      isCustom: true
    };
  });

  const frameworkManagementItems = [
    { title: toDisplayText(t('navigation.buildFramework', 'Build Framework'), lang), path: '/framework/builder', icon: Grid, collapsedIcon: Grid },
    { title: toDisplayText(t('navigation.manageFrameworks', 'Manage Frameworks'), lang), path: '/frameworks/manage', icon: Settings, collapsedIcon: Settings }
  ];

  const assessmentItems = [
    { title: toDisplayText(t('navigation.assessments', 'Assessments'), lang), path: '/create-assessment', icon: FileText, collapsedIcon: FileText }
  ];

  const accreditationItems = [
    { title: toDisplayText(t('accreditation.nav.dashboard', 'Dashboard'), lang), path: '/admin/accreditation', icon: LayoutDashboard, collapsedIcon: LayoutDashboard },
    { title: toDisplayText(t('accreditation.nav.applications', 'Applications'), lang), path: '/admin/accreditation/applications', icon: FileCheck, collapsedIcon: FileCheck },
    { title: toDisplayText(t('accreditation.nav.criteria', 'Criteria'), lang), path: '/admin/accreditation/criteria', icon: ListChecks, collapsedIcon: ListChecks },
    { title: toDisplayText(t('accreditation.nav.certificates', 'Certificates'), lang), path: '/admin/accreditation/certificates', icon: ScrollText, collapsedIcon: ScrollText },
    { title: toDisplayText(t('accreditation.nav.analytics', 'Analytics'), lang), path: '/admin/accreditation/analytics', icon: BarChart3, collapsedIcon: BarChart3 },
    { title: toDisplayText(t('accreditation.nav.benchmarks', 'Benchmarks'), lang), path: '/admin/accreditation/benchmarks', icon: Target, collapsedIcon: Target },
    { title: toDisplayText(t('accreditation.nav.compliance', 'Compliance'), lang), path: '/admin/accreditation/compliance', icon: ClipboardCheck, collapsedIcon: ClipboardCheck },
    { title: toDisplayText(t('accreditation.nav.audit', 'Audit Trail'), lang), path: '/admin/accreditation/audit', icon: History, collapsedIcon: History },
    { title: toDisplayText(t('accreditation.nav.settings', 'Settings'), lang), path: '/admin/accreditation/settings', icon: Settings, collapsedIcon: Settings }
  ];

  const administrationItems = [
    { title: toDisplayText(t('admin.userManagement', 'User Management'), lang), path: '/admin/users', icon: Users, collapsedIcon: Users },
    { title: toDisplayText(t('admin.permissionsManagement', 'Permissions'), lang), path: '/admin/permissions', icon: Shield, collapsedIcon: Shield },
    { title: toDisplayText(t('admin.organizationManagement', 'Organization Management'), lang), path: '/admin/organizations', icon: Building2, collapsedIcon: Building2 }
  ];

  const dataManagementItems = [
    { title: toDisplayText(t('admin.backup', 'Backup'), lang), path: '/admin/backup', icon: Database, collapsedIcon: Database },
    { title: toDisplayText(t('admin.restore', 'Restore'), lang), path: '/admin/restore', icon: ArchiveRestore, collapsedIcon: ArchiveRestore }
  ];

  const systemItems = [
    { title: toDisplayText(t('AI Settings', 'AI Settings'), lang), path: '/admin/ai-agent-config', icon: Bot, collapsedIcon: Bot, adminOnly: true },
    { title: toDisplayText(t('common.settings', 'Settings'), lang), path: '/settings', icon: Settings, collapsedIcon: Settings },
    { title: toDisplayText(t('navigation.manual', 'Manual'), lang), path: '/manual', icon: BookOpen, collapsedIcon: BookOpen },
    { title: toDisplayText(t('common.help', 'Help'), lang), path: '/help', icon: LifeBuoy, collapsedIcon: LifeBuoy }
  ];

  const selfAssessmentAdminItems = [
    { title: toDisplayText(t('admin.selfAssessmentUsers', 'Self Assessment Users'), lang), path: '/admin/self-assessment/users', icon: UserCircle, collapsedIcon: UserCircle, adminOnly: true },
    { title: toDisplayText(t('admin.selfAssessmentOrganizations', 'Self Assessment Organizations'), lang), path: '/admin/self-assessment/organizations', icon: Building2, collapsedIcon: Building2, adminOnly: true },
    { title: toDisplayText(t('admin.assessmentActivity', 'Assessment Activity'), lang), path: '/admin/self-assessment/activity', icon: Activity, collapsedIcon: Activity, adminOnly: true },
    { title: toDisplayText(t('admin.rankingsLeaderboards', 'Rankings & Leaderboards'), lang), path: '/admin/self-assessment/rankings', icon: Trophy, collapsedIcon: Trophy, adminOnly: true },
    { title: toDisplayText(t('admin.leadScoring', 'Lead Scoring'), lang), path: '/admin/demo/lead-scores', icon: Flame, collapsedIcon: Flame, adminOnly: true },
    { title: toDisplayText(t('admin.emailCampaigns', 'Email Campaigns'), lang), path: '/admin/demo/email-campaigns', icon: Mail, collapsedIcon: Mail, adminOnly: true },
    { title: toDisplayText(t('admin.selfAssessmentConfiguration', 'Self Assessment Configuration'), lang), path: '/admin/self-assessment/config', icon: Cog, collapsedIcon: Cog, adminOnly: true },
    { title: toDisplayText(t('admin.organizationGroups', 'Organization Groups'), lang), path: '/admin/org-groups', icon: Users, collapsedIcon: Users, adminOnly: true }
  ];

  const shouldShowDemoAdmin = isSystemAdmin || user?.role === 'System Admin' || user?.title_code === 'SYS_ADMIN';
  const isSelfAssessmentUser = user?.role === 'self_assessor' || user?.role === 'Demo Assessor' || user?.account_type === 'self_assessor';
  const isOrgAdmin = user?.role === 'org_admin';

  const allSections = [
    { key: 'core', title: toDisplayText(t('navigation.core', 'Core'), lang), items: coreItems },
    { key: 'frameworks', title: toDisplayText(t('navigation.frameworks', 'Frameworks'), lang), items: customFrameworkItems },
    { key: 'frameworkManagement', title: toDisplayText(t('navigation.frameworkManagement', 'Framework Management'), lang), items: frameworkManagementItems },
    { key: 'assessments', title: toDisplayText(t('navigation.assessments', 'Assessments'), lang), items: assessmentItems },
    { key: 'accreditation', title: toDisplayText(t('accreditation.nav.title', 'Accreditation'), lang), items: accreditationItems },
    ...(shouldShowDemoAdmin ? [{ key: 'demo', title: toDisplayText(t('navigation.selfAssessment', 'Self Assessment'), lang), items: selfAssessmentAdminItems }] : []),
    { key: 'administration', title: toDisplayText(t('navigation.administration', 'Administration'), lang), items: administrationItems },
    { key: 'dataManagement', title: toDisplayText(t('navigation.dataManagement', 'Data Management'), lang), items: dataManagementItems },
    { key: 'system', title: toDisplayText(t('navigation.system', 'System'), lang), items: systemItems }
  ];

  const selfAssessmentSections = ['core', 'system'];
  const orgAdminSections = ['core', 'frameworks', 'assessments', 'system'];
  const sections = isSelfAssessmentUser
    ? allSections.filter(s => selfAssessmentSections.includes(s.key))
    : isOrgAdmin
    ? allSections.filter(s => orgAdminSections.includes(s.key))
    : allSections;

  const isActive = (path) => {
    if (path.includes('?')) {
      return location.pathname + location.search === path;
    }
    return location.pathname === path;
  };

  const getRoutePermission = (path) => {
    const permissionMap = {
      '/admin/users': { resource: 'users', action: 'read' },
      '/admin/organizations': { resource: 'organizations', action: 'read' },
      '/admin/backup': { resource: 'settings', action: 'read' },
      '/admin/restore': { resource: 'settings', action: 'read' },
      '/create-assessment': { resource: 'assessments', action: 'create' },
      '/settings': { resource: 'settings', action: 'read' }
    };
    return permissionMap[path];
  };

  const isItemVisible = (item) => {
    const isAdmin = isSystemAdmin || user?.role === 'System Admin' || user?.title_code === 'SYS_ADMIN';
    if (item.adminOnly && !isAdmin) return false;
    if (isAdmin) return true;
    const routePermission = getRoutePermission(item.path);
    if (routePermission) {
      try {
        return hasPermission(routePermission.resource, routePermission.action);
      } catch (error) {
        console.warn('Permission check error for', item.path, error);
        return false;
      }
    }
    return true;
  };

  // Flatten all visible items for command palette
  const getAllVisibleItems = () => {
    const items = [];
    sections.forEach(section => {
      section.items.filter(isItemVisible).forEach(item => {
        items.push({
          ...item,
          sectionTitle: section.title,
          label: typeof item.title === 'string' ? item.title : toDisplayText(item.title, lang) || ''
        });
      });
    });
    return items;
  };

  // Command palette results
  const paletteItems = getAllVisibleItems();
  const paletteResults = paletteQuery
    ? paletteItems.filter(i =>
        i.label.toLowerCase().includes(paletteQuery.toLowerCase()) ||
        (i.sectionTitle && i.sectionTitle.toLowerCase().includes(paletteQuery.toLowerCase()))
      )
    : paletteItems.slice(0, 8);

  // Palette keyboard navigation
  const handlePaletteKeyDown = (e) => {
    if (e.key === 'Escape') {
      setPaletteOpen(false);
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      setPaletteHighlight(prev => Math.min(prev + 1, paletteResults.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setPaletteHighlight(prev => Math.max(prev - 1, 0));
    } else if (e.key === 'Enter' && paletteResults[paletteHighlight]) {
      e.preventDefault();
      navigate(paletteResults[paletteHighlight].path);
      setPaletteOpen(false);
    }
  };

  // Render a single nav item
  const renderNavItem = (item) => {
    if (!isItemVisible(item)) return null;

    const Icon = item.icon;
    const isItemActive = isActive(item.path);
    const label = typeof item.title === 'string' ? item.title : toDisplayText(item.title, i18n.language) || '';

    return (
      <Link
        key={item.path}
        to={item.path}
        className={`nav-item ${isItemActive ? 'active' : ''}`}
        onMouseEnter={(e) => showTooltip(e, label)}
        onMouseLeave={hideTooltip}
        title={isCollapsed ? label : ''}
      >
        <div className="nav-item-icon" aria-hidden="true">
          <Icon size={17} />
        </div>
        {!isCollapsed && (
          <span className="nav-item-text">{label}</span>
        )}
      </Link>
    );
  };

  // Render a section with collapsible header
  const renderSection = (section, index) => {
    const isExpanded = expandedSections[section.key];
    const visibleItems = section.items.filter(isItemVisible);
    if (visibleItems.length === 0) return null;

    return (
      <div key={section.key} className="nav-section" data-section={section.key}>
        {!isCollapsed && (
          <button
            className="nav-section-header"
            onClick={() => toggleSection(section.key)}
          >
            <span className="nav-section-title">{toDisplayText(section.title, lang)}</span>
            <span className={`nav-section-caret ${isExpanded ? 'expanded' : ''}`}>
              <ChevronDown size={14} />
            </span>
          </button>
        )}
        {isCollapsed && index > 0 && (
          <div className="nav-section-divider" />
        )}
        {(isExpanded || isCollapsed) && (
          <div className="nav-section-content">
            {visibleItems.map(renderNavItem)}
          </div>
        )}
      </div>
    );
  };

  return (
    <>
      <div className="main-sidebar">
          {/* Header */}
          <div className="sidebar-header">
            {!isCollapsed ? (
              <>
                <div className="sidebar-brand">
                  <div className={`sidebar-logo ${logoPreview ? 'has-image' : ''}`}>
                    {logoPreview ? (
                      <img src={logoPreview} alt="Logo" className="sidebar-logo-img" />
                    ) : (
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                        <rect x="3" y="3" width="7" height="7" rx="1" fill="currentColor" />
                        <rect x="14" y="3" width="7" height="7" rx="1" fill="currentColor" opacity="0.6" />
                        <rect x="3" y="14" width="7" height="7" rx="1" fill="currentColor" opacity="0.6" />
                        <rect x="14" y="14" width="7" height="7" rx="1" fill="currentColor" />
                      </svg>
                    )}
                  </div>
                  <span className="sidebar-brand-name">Matrix</span>
                </div>
                <button className="sidebar-collapse-btn" onClick={onToggle} title={isRTL ? '' : 'Collapse'}>
                  <PanelLeftClose size={16} />
                </button>
                {isMobile && (
                  <button
                    className="mobile-close-btn"
                    onClick={onToggle}
                    aria-label={t('common.close', 'Close')}
                  >
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <line x1="18" y1="6" x2="6" y2="18"></line>
                      <line x1="6" y1="6" x2="18" y2="18"></line>
                    </svg>
                  </button>
                )}
              </>
            ) : (
              <div className={`sidebar-logo collapsed-logo ${logoPreview ? 'has-image' : ''}`}>
                {logoPreview ? (
                  <img src={logoPreview} alt="Logo" className="sidebar-logo-img" />
                ) : (
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                    <rect x="3" y="3" width="7" height="7" rx="1" fill="currentColor" />
                    <rect x="14" y="3" width="7" height="7" rx="1" fill="currentColor" opacity="0.6" />
                    <rect x="3" y="14" width="7" height="7" rx="1" fill="currentColor" opacity="0.6" />
                    <rect x="14" y="14" width="7" height="7" rx="1" fill="currentColor" />
                  </svg>
                )}
              </div>
            )}
          </div>

          {/* Search trigger */}
          <div className="sidebar-search">
            <button className="sidebar-search-trigger" onClick={() => setPaletteOpen(true)}>
              <Search size={15} />
              {!isCollapsed && (
                <>
                  <span className="sidebar-search-placeholder">{isRTL ? 'بحث...' : `${t('common.search', 'Search')}...`}</span>
                  <kbd className="sidebar-search-kbd">⌘K</kbd>
                </>
              )}
            </button>
          </div>

          {/* Navigation */}
          <nav className="sidebar-content">
            {sections.map((section, index) => renderSection(section, index))}
          </nav>

          {/* Collapsed expand button */}
          {isCollapsed && !isMobile && (
            <div className="sidebar-expand-footer">
              <button className="sidebar-expand-btn" onClick={onToggle} title={isRTL ? '' : 'Expand'}>
                <PanelLeftOpen size={16} />
              </button>
            </div>
          )}
        </div>

      {/* Floating tooltip for collapsed mode */}
      {tooltip.visible && isCollapsed && (
        <div
          className={`sidebar-tooltip ${tooltip.isRTL ? 'rtl' : ''}`}
          style={{
            position: 'fixed',
            top: tooltip.top,
            left: tooltip.left,
            right: tooltip.right,
            transform: 'translateY(-50%)',
          }}
        >
          {tooltip.text}
        </div>
      )}

      {/* Command Palette (portal-style overlay) */}
      {paletteOpen && (
        <div className="cmd-palette-backdrop" onClick={() => setPaletteOpen(false)}>
          <div className="cmd-palette" onClick={e => e.stopPropagation()} onKeyDown={handlePaletteKeyDown}>
            <div className="cmd-palette-input-row">
              <Search size={17} className="cmd-palette-search-icon" />
              <input
                ref={paletteInputRef}
                className="cmd-palette-input"
                value={paletteQuery}
                onChange={e => { setPaletteQuery(e.target.value); setPaletteHighlight(0); }}
                placeholder={isRTL ? 'ابحث عن صفحة، أداة، أو إجراء...' : 'Search pages, tools, or actions...'}
              />
              <kbd className="cmd-palette-esc">ESC</kbd>
            </div>
            <div className="cmd-palette-results">
              {paletteResults.length === 0 ? (
                <div className="cmd-palette-empty">
                  {isRTL ? 'لا توجد نتائج' : 'No results found'}
                </div>
              ) : (
                paletteResults.map((item, idx) => {
                  const ItemIcon = item.icon;
                  return (
                    <button
                      key={item.path + idx}
                      className={`cmd-palette-item ${idx === paletteHighlight ? 'highlighted' : ''}`}
                      onClick={() => { navigate(item.path); setPaletteOpen(false); }}
                      onMouseEnter={() => setPaletteHighlight(idx)}
                    >
                      <span className="cmd-palette-item-icon"><ItemIcon size={16} /></span>
                      <span className="cmd-palette-item-label">{item.label}</span>
                      <span className="cmd-palette-item-group">{item.sectionTitle}</span>
                    </button>
                  );
                })
              )}
            </div>
            <div className="cmd-palette-footer">
              <span>↵ {isRTL ? 'افتح' : 'Open'}</span>
              <span>↑↓ {isRTL ? 'تنقل' : 'Navigate'}</span>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ModernSidebar;
